// FILE: frontend/src/components/Navbar.tsx
import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Coins,
  LayoutDashboard,
  LogIn,
  LogOut,
  Menu,
  Settings,
  Sparkles,
  UserPlus,
  Video,
  X,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import ThemeSwitch from "./ThemeSwitch";

function isAdminUser(user: any): boolean {
  return Boolean(
    user?.isAdmin ||
      user?.claims?.admin ||
      user?.customClaims?.admin ||
      user?.role === "admin"
  );
}

export default function Navbar() {
  const { user, logout } = useAuth() as any;

  const isAdmin = useMemo(() => isAdminUser(user), [user]);

  const [open, setOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (!target?.closest("[data-user-menu]")) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeMenu = () => {
    setOpen(false);
    setShowUserMenu(false);
  };

  const handleLogout = async () => {
    closeMenu();

    try {
      await logout();
    } catch (error) {
      console.error("Failed to sign out:", error);
    }
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
      "transition-colors",
      isActive
        ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
        : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
    ].join(" ");

  const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium",
      "transition-colors",
      isActive
        ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
        : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
    ].join(" ");

  const userLabel =
    user?.displayName ||
    user?.email?.split("@")[0] ||
    "Account";

  const avatarLabel = String(userLabel).charAt(0).toUpperCase();

  return (
    <nav className="relative">
      <div className="flex min-h-[56px] items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-6">
          <NavLink
            to="/"
            className="flex shrink-0 items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-xl font-bold text-transparent"
            onClick={closeMenu}
          >
            <Sparkles
              size={24}
              className="text-blue-600 dark:text-blue-400"
            />
            <span className="hidden sm:inline">AutoQuiz AI</span>
          </NavLink>

          <div className="hidden items-center gap-1 lg:flex">
            <NavLink to="/animated-quiz" className={linkClass}>
              <Sparkles size={16} />
              Create
            </NavLink>

            <NavLink to="/flashcards" className={linkClass}>
              Flashcards
            </NavLink>

            <NavLink to="/dashboard" className={linkClass}>
              <Video size={16} />
              My Videos
            </NavLink>

            <NavLink to="/credits" className={linkClass}>
              <Coins size={16} />
              Credits
            </NavLink>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:block">
            <ThemeSwitch />
          </div>

          {user ? (
            <div className="relative hidden md:block" data-user-menu>
              <button
                type="button"
                onClick={() => setShowUserMenu((current) => !current)}
                className="flex h-11 min-w-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-3 font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
                title={user?.email || "Account"}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-sm font-bold text-white">
                  {avatarLabel}
                </span>
              </button>

              {showUserMenu && (
                <div
                  className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
                  role="menu"
                >
                  <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                    <div className="truncate text-sm font-semibold">
                      {userLabel}
                    </div>

                    {user?.email && (
                      <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                        {user.email}
                      </div>
                    )}

                    {isAdmin && (
                      <div className="mt-2 inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                        Admin
                      </div>
                    )}
                  </div>

                  <div className="p-2">
                    <NavLink
                      to="/account"
                      onClick={closeMenu}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <Settings size={16} />
                      Account
                    </NavLink>

                    {isAdmin && (
                      <NavLink
                        to="/admin/layout"
                        onClick={closeMenu}
                        className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <LayoutDashboard size={16} />
                        Admin Layout
                      </NavLink>
                    )}

                    <button
                      type="button"
                      onClick={() => void handleLogout()}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden items-center gap-2 md:flex">
              <NavLink
                to="/login"
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <LogIn size={16} />
                Sign In
              </NavLink>

              <NavLink
                to="/login"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <UserPlus size={16} />
                Sign Up
              </NavLink>
            </div>
          )}

          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div
        className={[
          "overflow-hidden transition-all duration-300 lg:hidden",
          open
            ? "max-h-[700px] opacity-100"
            : "max-h-0 opacity-0",
        ].join(" ")}
      >
        <div className="space-y-4 border-t border-slate-200 py-4 dark:border-slate-700">
          <div className="space-y-1 px-2">
            <NavLink
              to="/animated-quiz"
              className={mobileLinkClass}
              onClick={closeMenu}
            >
              <Sparkles size={18} />
              Create
            </NavLink>

            <NavLink
              to="/flashcards"
              className={mobileLinkClass}
              onClick={closeMenu}
            >
              Flashcards
            </NavLink>

            <NavLink
              to="/dashboard"
              className={mobileLinkClass}
              onClick={closeMenu}
            >
              <Video size={18} />
              My Videos
            </NavLink>

            <NavLink
              to="/credits"
              className={mobileLinkClass}
              onClick={closeMenu}
            >
              <Coins size={18} />
              Credits
            </NavLink>
          </div>

          <div className="border-t border-slate-200 px-2 pt-4 dark:border-slate-700">
            <div className="mb-3 flex items-center justify-between px-3">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                Theme
              </span>
              <ThemeSwitch />
            </div>

            {user ? (
              <div className="space-y-1">
                <div className="mb-2 px-3 py-2">
                  <div className="truncate text-sm font-semibold">
                    {userLabel}
                  </div>

                  {user?.email && (
                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                      {user.email}
                    </div>
                  )}
                </div>

                <NavLink
                  to="/account"
                  className={mobileLinkClass}
                  onClick={closeMenu}
                >
                  <Settings size={18} />
                  Account
                </NavLink>

                {isAdmin && (
                  <NavLink
                    to="/admin/layout"
                    className={mobileLinkClass}
                    onClick={closeMenu}
                  >
                    <LayoutDashboard size={18} />
                    Admin Layout
                  </NavLink>
                )}

                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                >
                  <LogOut size={18} />
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <NavLink
                  to="/login"
                  className={mobileLinkClass}
                  onClick={closeMenu}
                >
                  <LogIn size={18} />
                  Sign In
                </NavLink>

                <NavLink
                  to="/login"
                  className={mobileLinkClass}
                  onClick={closeMenu}
                >
                  <UserPlus size={18} />
                  Sign Up
                </NavLink>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
